import { Loader2 } from "lucide-react";
import useSWRImmutable from "swr/immutable";
import { cn } from "~/lib/utils";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";
import { McpAppPaneFrame } from "./McpAppPaneFrame";
import { fetchProjectAppPanel, loadProjectApp, PROJECT_APPS_SERVER_ID } from "./projectApps";

interface ProjectAppFrameProps {
  // The project's UUID, which the project apps server takes.
  projectId: string;
  slug: string;
  // The session holding a draft of the app; absent for a published app.
  draftIn?: string;
  title: string;
  // Changes when a new template is saved or published, so it loads in a fresh frame.
  version: string;
  className?: string;
}

/**
 * An app opened by its host rather than by an agent: a published app on its
 * page, or a draft in its session. Its panel comes from the project apps
 * server and runs through the same MCP Apps bridge as the session pane.
 */
export const ProjectAppFrame = ({
  projectId,
  slug,
  draftIn,
  title,
  version,
  className,
}: ProjectAppFrameProps) => {
  const key = [projectId, slug, draftIn, version];
  const panel = useSWRImmutable(["project-app-panel", ...key], () =>
    fetchProjectAppPanel(projectId, slug, draftIn)
  );
  const envelope = useSWRImmutable(["project-app-envelope", ...key], () =>
    loadProjectApp(projectId, slug, { conversationId: draftIn, draft: draftIn !== undefined })
  );

  const error = panel.error ?? envelope.error;
  if (error) {
    return (
      <div className={cn("flex items-center justify-center text-sm text-destructive", className)}>
        {isApiError(error) ? getApiErrorMessage(error) : "This app couldn't be loaded."}
      </div>
    );
  }
  if (!panel.data || !envelope.data) {
    return (
      <div className={cn("flex items-center justify-center", className)}>
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return (
    <div className={cn("min-h-0", className)}>
      <McpAppPaneFrame
        key={version}
        html={panel.data.html}
        url={panel.data.url}
        title={title}
        serverId={PROJECT_APPS_SERVER_ID}
        state={envelope.data}
      />
    </div>
  );
};
